import { Trans, useTranslation } from 'react-i18next';
import { cn, Tooltip } from '@c2fo/liquidity';
import BuyerLogo from '@/enterprise/components/BuyerLogo';
import { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useLocaleFormat } from '@/utils/useLocaleFormat';
import useStableGroupByBuyer from '../useStableGroupByBuyer';

const splitBuyers = (buyers: BuyerGroupWithStats[], limit: number) => {
  const visibleBuyers = buyers.slice(0, limit);
  const remainingBuyers = buyers.slice(limit);

  return { visibleBuyers, remainingBuyers };
};

const BuyerNamesTooltip = ({ buyers, children }: { buyers: BuyerGroupWithStats[]; children: React.ReactNode }) => {
  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <span>{children}</span>
      </Tooltip.Trigger>
      <Tooltip.Content arrow className="max-w-64 text-center font-normal" variant="dark">
        <p>{buyers.map((buyer) => buyer.makerOrganizationName).join(', ')}</p>
      </Tooltip.Content>
    </Tooltip>
  );
};

const BuyerLogoList = ({ takerMarkets }: { takerMarkets: TakerMarket[] }) => {
  const { t } = useTranslation();
  const { asNumber } = useLocaleFormat();
  const buyers = useStableGroupByBuyer({ takerMarkets });
  const { visibleBuyers, remainingBuyers } = splitBuyers(buyers, 2);
  const avatarMap = [...visibleBuyers].reverse(); // reverse order so avatars overlap from left to right

  return (
    <div className="flex items-center gap-2">
      <div className="hidden sm:flex sm:flex-row-reverse">
        {remainingBuyers.length > 0 && (
          <BuyerNamesTooltip buyers={remainingBuyers}>
            <div className="relative -ml-4 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-[0.5px] border-stroke bg-gray-100 text-xl font-medium">
              +{remainingBuyers.length}
            </div>
          </BuyerNamesTooltip>
        )}
        {avatarMap.map((buyer, idx) => (
          <div key={buyer.makerOrganizationUuid} className={cn('flex items-center', { '-mr-4': idx > 0 })}>
            <BuyerLogo buyerLogoUrl={buyer.makerOrganizationLogo} buyerName={buyer.makerOrganizationName} size="lg" />
          </div>
        ))}
      </div>
      <div className="text-xl font-medium leading-tight">
        {remainingBuyers.length === 0 ? (
          t('builder.buyerList', {
            buyerName1: visibleBuyers[0]?.makerOrganizationName,
            buyerName2: visibleBuyers[1]?.makerOrganizationName,
            count: visibleBuyers.length,
          })
        ) : (
          <Trans
            i18nKey="builder.buyerListWithRemaining"
            values={{
              buyerName1: visibleBuyers[0]?.makerOrganizationName,
              buyerName2: visibleBuyers[1]?.makerOrganizationName,
              formattedCount: asNumber({ value: remainingBuyers.length }),
            }}
            components={{
              style: (
                <BuyerNamesTooltip buyers={remainingBuyers}>
                  {/* placeholder replaced by translation when rendered */}
                  <span />
                </BuyerNamesTooltip>
              ),
            }}
          />
        )}
      </div>
    </div>
  );
};

export default BuyerLogoList;
